// ============================================================================
// GEO SERVICE - Location Verification for Sweepstakes Compliance
//  SECURITY FIX: Now FAILS CLOSED (blocks access on error instead of allowing)

//  ADMIN BYPASS: Checks user document for isAdmin/bypassGeoRestriction flags

// ============================================================================
// This service handles:
// - IP-based geolocation verification
// - US-only access enforcement
// - Restricted state blocking (WA, ID, CA, CT, MT, NJ, MI, NV, NY)
// - VPN detection (when available)
// - Admin bypass checking
// - Logging all geo checks for compliance auditing
// ============================================================================

import { db, auth } from './firebase';
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';

// ============================================================================
// CONFIGURATION
// ============================================================================

const GEO_CONFIG = {
  // IP geolocation API (free tier)
  API_URL: 'https://ipapi.co/json/',
  
  // Cache duration (10 minutes)
  CACHE_DURATION_MS: 10 * 60 * 1000,
  
  // Allowed country
  ALLOWED_COUNTRY: 'US',
  
  // Restricted states (sweepstakes not allowed)
  //  UPDATED: Added CA, CT, MT, NJ, MI, NV, NY to restricted states

  // Must stay in sync with RESTRICTED_STATES in functions/src/index.ts.
  // The server is authoritative; this list drives UI messaging.
  RESTRICTED_STATES: [
    // Statutory bans or active enforcement (14)
    'WA', 'ID', 'CA', 'CT', 'MT', 'NJ', 'MI', 'NV', 'NY',
    'IN', 'LA', 'ME', 'OK', 'TN',
    // Operator-excluded gray-area states (7), September 2026
    'DE', 'MS', 'WV', 'MD', 'AZ', 'KY', 'PA',
  ],
  
  // Request timeout
  TIMEOUT_MS: 10000,
  
  //  SECURITY: Whether to fail closed (block on error) or open (allow on error)

  // ALWAYS set to true for production - this is critical for compliance
  FAIL_CLOSED: true,
};

// ============================================================================
// INTERFACES
// ============================================================================

export interface GeoCheckResult {
  allowed: boolean;
  country?: string;
  countryName?: string;
  region?: string;
  regionName?: string;
  city?: string;
  ip?: string;
  reason?: string;
  isVPN?: boolean;
  timestamp: Date;
  cached?: boolean;
  error?: string;
  //  NEW: Additional fields for geo restrictions

  isRestrictedState?: boolean;
  canPurchasePKCash?: boolean;
  canRedeemPKCash?: boolean;
  canPlayPKCash?: boolean;  //  NEW: Can play PKCash games

  adminBypassed?: boolean;
}

interface GeoApiResponse {
  ip: string;
  country: string;
  country_name: string;
  region: string;
  region_code: string;
  city: string;
  timezone: string;
  org: string;
  asn: string;
  // VPN detection (not available in free tier)
  threat?: {
    is_vpn?: boolean;
    is_proxy?: boolean;
    is_tor?: boolean;
    is_datacenter?: boolean;
  };
  error?: boolean;
  reason?: string;
}

// ============================================================================
// GEO SERVICE CLASS
// ============================================================================

class GeoService {
  private cache: GeoCheckResult | null = null;
  private cacheTimestamp: number = 0;
  private bypassCache: { hasBypass: boolean; checkedAt: number } | null = null;

  // ============================================================================
  // ADMIN BYPASS CHECK
  // ============================================================================

  /**
   * Check if current user has admin bypass enabled
   *  This is checked FIRST before any geo restrictions

   */
  private async checkAdminBypass(): Promise<boolean> {
    try {
      // Check bypass cache (valid for 5 minutes)
      if (this.bypassCache && Date.now() - this.bypassCache.checkedAt < 5 * 60 * 1000) {
        return this.bypassCache.hasBypass;
      }

      const user = auth.currentUser;
      if (!user) {
        this.bypassCache = { hasBypass: false, checkedAt: Date.now() };
        return false;
      }

      // Bounded read: this runs BEFORE the geo API call, and an unbounded
      // getDoc queued behind a congested backend hung the entire location
      // step ('Checking location...' forever). Timing out just means no
      // bypass - restrictions apply, which is always the safe answer.
      const userDoc = await Promise.race([
        getDoc(doc(db, 'users', user.uid)),
        new Promise<null>(res => setTimeout(() => res(null), 3000)),
      ]);
      if (!userDoc || !userDoc.exists()) {
        this.bypassCache = { hasBypass: false, checkedAt: Date.now() };
        return false;
      }

      const userData = userDoc.data();
      const hasBypass = userData?.isAdmin === true || userData?.bypassGeoRestriction === true;
      
      this.bypassCache = { hasBypass, checkedAt: Date.now() };
      
      if (hasBypass) {
        console.log(`✅ [GeoService] Admin bypass active for user ${user.uid.substring(0, 8)}`);
      }

      return hasBypass;
    } catch (error) {
      console.error('❌ [GeoService] Error checking admin bypass:', error);
      return false;
    }
  }

  // ============================================================================
  // DYNAMIC RESTRICTION CHECK (from platformSettings)
  // ============================================================================

  private dynamicRestrictionsCache: { states: string[]; checkedAt: number } | null = null;

  /**
   * Check if a state is dynamically restricted via platformSettings
   *  This allows admins to add/remove state restrictions without code changes

   */
  private async checkDynamicRestriction(stateCode: string): Promise<boolean> {
    try {
      // Check cache (valid for 5 minutes)
      if (this.dynamicRestrictionsCache && Date.now() - this.dynamicRestrictionsCache.checkedAt < 5 * 60 * 1000) {
        return this.dynamicRestrictionsCache.states.includes(stateCode);
      }

      // Fetch platformSettings from Firestore
      const settingsDoc = await getDoc(doc(db, 'platformSettings', 'main'));
      if (!settingsDoc.exists()) {
        this.dynamicRestrictionsCache = { states: [], checkedAt: Date.now() };
        return false;
      }

      const settings = settingsDoc.data();
      const restrictedStates: string[] = settings?.restrictedStates || [];
      
      // Cache the result
      this.dynamicRestrictionsCache = { states: restrictedStates, checkedAt: Date.now() };
      
      console.log(`📍 [GeoService] Dynamic restrictions loaded: ${restrictedStates.join(', ') || 'None'}`);
      
      return restrictedStates.includes(stateCode);
    } catch (error) {
      console.error('❌ [GeoService] Error checking dynamic restrictions:', error);
      return false; // Don't block on error - hardcoded list still applies
    }
  }

  // ============================================================================
  // MAIN GEO CHECK METHOD
  // ============================================================================

  /**
   * Check if user's location is allowed for sweepstakes
   *  SECURITY FIX: Now fails CLOSED on any error

   *  ADMIN BYPASS: Checks user document for bypass flags first

   */
  async checkLocation(): Promise<GeoCheckResult> {
    try {
      //  ADMIN BYPASS: Check bypass FIRST

      const hasBypass = await this.checkAdminBypass();

      // Check cache first (but still need to check bypass)
      if (this.isCacheValid() && this.cache) {
        console.log('📍 [GeoService] Using cached geo result');
        const cachedResult = { ...this.cache, cached: true };
        
        //  If user has bypass, override restrictions

        if (hasBypass && cachedResult.isRestrictedState) {
          return {
            ...cachedResult,
            canPurchasePKCash: true,
            canRedeemPKCash: true,
            canPlayPKCash: true,
            adminBypassed: true,
          };
        }
        return cachedResult;
      }

      console.log('📍 [GeoService] Checking user location...');

      // Fetch geolocation data with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), GEO_CONFIG.TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(GEO_CONFIG.API_URL, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        //  SECURITY: Fail CLOSED on network error (unless bypass)

        console.error('❌ [GeoService] Network error during geo check:', fetchError);
        if (hasBypass) {
          return this.createBypassedResult('Network error but bypass active');
        }
        return this.createBlockedResult(
          'Unable to verify location. Please check your internet connection and try again.',
          'network_error'
        );
      }

      if (!response.ok) {
        //  SECURITY: Fail CLOSED on API error (unless bypass)

        console.error('❌ [GeoService] API error:', response.status);
        if (hasBypass) {
          return this.createBypassedResult('API error but bypass active');
        }
        return this.createBlockedResult(
          'Location verification service temporarily unavailable. Please try again later.',
          'api_error'
        );
      }

      const data: GeoApiResponse = await response.json();

      if (data.error) {
        //  SECURITY: Fail CLOSED on API-reported error (unless bypass)

        console.error('❌ [GeoService] API reported error:', data.reason);
        if (hasBypass) {
          return this.createBypassedResult('API reported error but bypass active');
        }
        return this.createBlockedResult(
          'Unable to verify your location. Please try again.',
          'api_reported_error'
        );
      }

      // Validate the response has required fields
      if (!data.country || !data.ip) {
        console.error('❌ [GeoService] Invalid API response - missing required fields');
        if (hasBypass) {
          return this.createBypassedResult('Invalid response but bypass active');
        }
        return this.createBlockedResult(
          'Location data incomplete. Please try again.',
          'invalid_response'
        );
      }

      // Process the geo check
      const result = await this.processGeoData(data, hasBypass);

      // Cache the result
      this.cache = result;
      this.cacheTimestamp = Date.now();

      // Log the check for compliance auditing
      await this.logGeoCheck(result);

      return result;

    } catch (error) {
      //  SECURITY: Fail CLOSED on any unexpected error

      console.error('❌ [GeoService] Unexpected error during geo check:', error);
      
      // Check bypass one more time
      const hasBypass = await this.checkAdminBypass();
      if (hasBypass) {
        return this.createBypassedResult('Unexpected error but bypass active');
      }
      
      return this.createBlockedResult(
        'An error occurred while verifying your location. Please try again.',
        'unexpected_error'
      );
    }
  }

  // ============================================================================
  // GEO DATA PROCESSING
  // ============================================================================

  /**
   * Process geo API response and determine if access is allowed
   */
  private async processGeoData(data: GeoApiResponse, hasBypass: boolean = false): Promise<GeoCheckResult> {
    const timestamp = new Date();

    // Check country
    if (data.country !== GEO_CONFIG.ALLOWED_COUNTRY) {
      console.log(`🚫 [GeoService] Blocked: Non-US country (${data.country})`);
      
      //  Admin bypass allows even from non-US (for testing)

      if (hasBypass) {
        console.log(`✅ [GeoService] Admin bypass - allowing non-US access`);
        return {
          allowed: true,
          country: data.country,
          countryName: data.country_name,
          region: data.region_code,
          regionName: data.region,
          city: data.city,
          ip: this.maskIP(data.ip),
          timestamp,
          isRestrictedState: false,
          canPurchasePKCash: true,
          canRedeemPKCash: true,
          canPlayPKCash: true,
          adminBypassed: true,
        };
      }
      
      return {
        allowed: false,
        country: data.country,
        countryName: data.country_name,
        region: data.region_code,
        regionName: data.region,
        city: data.city,
        ip: this.maskIP(data.ip),
        reason: `Sweepstakes not available in ${data.country_name || data.country}. Currently US-only.`,
        timestamp,
      };
    }

    // Check restricted states
    //  UPDATED: Block ALL PKCash activity in restricted states (unless bypass)

    //  DYNAMIC: Also check platformSettings.restrictedStates from Firestore

    const isHardcodedRestricted = GEO_CONFIG.RESTRICTED_STATES.includes(data.region_code);
    const isDynamicRestricted = await this.checkDynamicRestriction(data.region_code);
    const isRestrictedState = isHardcodedRestricted || isDynamicRestricted;
    
    if (isRestrictedState) {
      console.log(`📍 [GeoService] Restricted state (${data.region_code}) - PKCash blocked (hardcoded: ${isHardcodedRestricted}, dynamic: ${isDynamicRestricted})`);
      
      //  ADMIN BYPASS: Allow full access in restricted states

      if (hasBypass) {
        console.log(`✅ [GeoService] Admin bypass - allowing PKCash in ${data.region_code}`);
        return {
          allowed: true,
          country: data.country,
          countryName: data.country_name,
          region: data.region_code,
          regionName: data.region,
          city: data.city,
          ip: this.maskIP(data.ip),
          timestamp,
          isRestrictedState: true,  // Still mark as restricted for info
          canPurchasePKCash: true,
          canRedeemPKCash: true,
          canPlayPKCash: true,
          adminBypassed: true,
        };
      }
      
      return {
        allowed: true, //  Allow access to platform (PlayCoins only)

        country: data.country,
        countryName: data.country_name,
        region: data.region_code,
        regionName: data.region,
        city: data.city,
        ip: this.maskIP(data.ip),
        timestamp,
        isRestrictedState: true,
        canPurchasePKCash: false,  //  Cannot purchase in restricted state

        canRedeemPKCash: false,    //  Cannot redeem in restricted state

        canPlayPKCash: false,      //  Cannot play PKCash games

        reason: `PKCash is not available in ${data.region || data.region_code}. You can play with PlayCoins! Your PKCash balance will be available when you're in a legal state.`,
      };
    }

    // Check for VPN/proxy (if available in API response)
    const isVPN = data.threat?.is_vpn || 
                  data.threat?.is_proxy || 
                  data.threat?.is_tor || 
                  data.threat?.is_datacenter;

    if (isVPN && !hasBypass) {
      console.log(`🚫 [GeoService] Blocked: VPN/Proxy detected`);
      return {
        allowed: false,
        country: data.country,
        countryName: data.country_name,
        region: data.region_code,
        regionName: data.region,
        city: data.city,
        ip: this.maskIP(data.ip),
        isVPN: true,
        reason: 'VPN or proxy detected. Please disable VPN to access sweepstakes.',
        timestamp,
      };
    }

    // All checks passed
    console.log(`✅ [GeoService] Allowed: ${data.city}, ${data.region_code}, ${data.country}`);
    return {
      allowed: true,
      country: data.country,
      countryName: data.country_name,
      region: data.region_code,
      regionName: data.region,
      city: data.city,
      ip: this.maskIP(data.ip),
      isVPN: false,
      timestamp,
      //  Full access in legal states

      isRestrictedState: false,
      canPurchasePKCash: true,
      canRedeemPKCash: true,
      canPlayPKCash: true,
      adminBypassed: hasBypass,
    };
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   *  Create a bypassed result (for admin users)

   */
  private createBypassedResult(reason: string): GeoCheckResult {
    return {
      allowed: true,
      timestamp: new Date(),
      isRestrictedState: false,
      canPurchasePKCash: true,
      canRedeemPKCash: true,
      canPlayPKCash: true,
      adminBypassed: true,
      reason,
    };
  }

  /**
   *  SECURITY: Create a BLOCKED result (fail closed)

   * This is the default response when anything goes wrong
   */
  private createBlockedResult(reason: string, errorType?: string): GeoCheckResult {
    const result: GeoCheckResult = {
      allowed: false, //  SECURITY: Always block on error

      reason,
      timestamp: new Date(),
      error: errorType,
      canPlayPKCash: false,
      canPurchasePKCash: false,
      canRedeemPKCash: false,
    };

    // Log the blocked attempt
    this.logGeoCheck(result).catch(console.error);

    return result;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    if (!this.cache) return false;
    const age = Date.now() - this.cacheTimestamp;
    return age < GEO_CONFIG.CACHE_DURATION_MS;
  }

  /**
   * Mask IP address for privacy (show first two octets only)
   */
  private maskIP(ip: string): string {
    if (!ip) return 'unknown';
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.*.*`;
    }
    // IPv6 - just show first segment
    const v6parts = ip.split(':');
    if (v6parts.length > 1) {
      return `${v6parts[0]}:${v6parts[1]}:****`;
    }
    return 'masked';
  }

  /**
   * Log geo check for compliance auditing
   */
  private async logGeoCheck(result: GeoCheckResult): Promise<void> {
    try {
      await addDoc(collection(db, 'geoCheckLogs'), {
        allowed: result.allowed,
        country: result.country || null,
        region: result.region || null,
        city: result.city || null,
        ip: result.ip || null,
        reason: result.reason || null,
        isVPN: result.isVPN || false,
        error: result.error || null,
        cached: result.cached || false,
        isRestrictedState: result.isRestrictedState || false,
        adminBypassed: result.adminBypassed || false,
        timestamp: Timestamp.now(),
      });
    } catch (error) {
      // Don't fail the geo check if logging fails
      console.error('❌ [GeoService] Failed to log geo check:', error);
    }
  }

  /**
   * Clear the geo cache (useful for testing or after VPN toggle)
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
    this.bypassCache = null;
    console.log('🗑️ [GeoService] Cache cleared');
  }

  /**
   * Get cached result without making API call
   */
  getCachedResult(): GeoCheckResult | null {
    if (this.isCacheValid()) {
      return this.cache;
    }
    return null;
  }

  /**
   * Check if a specific state is restricted
   */
  isStateRestricted(stateCode: string): boolean {
    return GEO_CONFIG.RESTRICTED_STATES.includes(stateCode.toUpperCase());
  }

  /**
   * Get list of restricted states
   */
  getRestrictedStates(): string[] {
    return [...GEO_CONFIG.RESTRICTED_STATES];
  }

  // ============================================================================
  // QUICK CHECK METHODS (for UI)
  // ============================================================================

  /**
   * Quick check if user is allowed (uses cache if available)
   */
  async isAllowed(): Promise<boolean> {
    const result = await this.checkLocation();
    return result.allowed;
  }

  /**
   * Get blocking reason (if blocked)
   */
  async getBlockingReason(): Promise<string | null> {
    const result = await this.checkLocation();
    return result.allowed ? null : (result.reason || 'Access restricted');
  }

  /**
   * Get user's detected location
   */
  async getLocation(): Promise<{ city?: string; region?: string; country?: string } | null> {
    const result = await this.checkLocation();
    if (!result.city && !result.region && !result.country) {
      return null;
    }
    return {
      city: result.city,
      region: result.regionName || result.region,
      country: result.countryName || result.country,
    };
  }

  /**
   * Alias for checkLocation - for backward compatibility
   * @deprecated Use checkLocation() instead
   */
  async checkIP(): Promise<GeoCheckResult> {
    return this.checkLocation();
  }

  // ============================================================================
  //  GEO RESTRICTION HELPERS

  // ============================================================================

  /**
   * Check location with admin bypass support
   * Call this when you need to check if a specific user has bypass privileges
   */
  async checkLocationWithBypass(userId?: string): Promise<GeoCheckResult> {
    // checkLocation now handles bypass internally
    return this.checkLocation();
  }

  /**
   * Quick check if user can play PKCash
   */
  async canPlayPKCash(): Promise<boolean> {
    const result = await this.checkLocation();
    return result.canPlayPKCash === true;
  }

  /**
   * Quick check if user can redeem PKCash
   */
  async canRedeemPKCash(): Promise<boolean> {
    const result = await this.checkLocation();
    return result.canRedeemPKCash === true;
  }

  /**
   * Check if current location is in a restricted state
   */
  isInRestrictedState(): boolean {
    return this.cache?.isRestrictedState === true && !this.cache?.adminBypassed;
  }

  /**
   * Get current state code from cache
   */
  getCurrentStateCode(): string | undefined {
    return this.cache?.region;
  }

  /**
   * Get restricted state display names
   */
  getRestrictedStateNames(): { [key: string]: string } {
    return {
      'WA': 'Washington',
      'ID': 'Idaho',
      'CA': 'California',
      'CT': 'Connecticut',
      'MT': 'Montana',
      'NJ': 'New Jersey',
      'MI': 'Michigan',
      'NV': 'Nevada',
      'NY': 'New York',
      'IN': 'Indiana',
      'LA': 'Louisiana',
      'ME': 'Maine',
      'OK': 'Oklahoma',
      'TN': 'Tennessee',
    };
  }

  /**
   * Get user-friendly message for restricted state
   */
  getRestrictedStateMessage(stateCode: string): string {
    const names = this.getRestrictedStateNames();
    const stateName = names[stateCode] || stateCode;
    return `PKCash is not available in ${stateName}. You can play with PlayCoins! Your PKCash balance will be available when you're in a legal state.`;
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const geoService = new GeoService();
export default geoService;
export { GEO_CONFIG };